import { Component, OnInit, Input } from '@angular/core';
import { BaseComponentDirective } from '@core/component/base.component';
import { LeadListService } from '@app/lead/services/leadlist-service';
import { first, takeUntil } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { LeadViewModel } from '@app/lead/model/lead-view.model';
import { Subscription } from 'rxjs';
import { DialerContentService } from '@app/shared/services/dialer-content-service';
import { NpiProvider, NpiSelectionEvent } from '@app/shared/components/npi-lookup/npi-provider.model';

export interface ProviderData {
    npi: string;
    name: string;
    type: string;
    credential: string;
    phone: string;
    fax: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    notes: string;
    source: 'npi_lookup' | 'manual_entry';
}

@Component({
    selector: 'app-lead-providers',
    templateUrl: './lead-providers.component.html',
    styleUrls: ['./lead-providers.component.scss'],
})
export class LeadProvidersComponent extends BaseComponentDirective implements OnInit {
    @Input() leadId: string;

    leadProviders: any[] = [];
    loadingData = false;
    editMode = false;
    editId: string = null;
    providerNotes = '';
    selectedProvider: NpiProvider = null;
    selectedSource: 'npi_lookup' | 'manual_entry' = 'npi_lookup';

    dialerSubscribe: Subscription;

    constructor(
        private leadService: LeadListService,
        private confirmationService: ConfirmationService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private dialerContentService: DialerContentService,
    ) {
        super();
        this.dialerSubscribe = this.dialerContentService.getUpdate().pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            this.leadId = message.text;
            this.getProvidersList();
        });
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.getProvidersList();
    }

    getProvidersList(): void {
        this.loadingData = true;
        this.leadService
            .getLeadProviders(this.leadId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: (result) => {
                    this.loadingData = false;
                    this.leadProviders = result;
                },
                error: () => {
                    this.loadingData = false;
                    this.leadProviders = [];
                },
            });
    }

    parseProvider(value: string): ProviderData | null {
        if (!value) return null;
        try {
            const parsed = JSON.parse(value);
            if (parsed && parsed.name) return parsed;
            return null;
        } catch {
            return null;
        }
    }

    onProviderSelected(event: NpiSelectionEvent): void {
        this.selectedProvider = event.provider;
        this.selectedSource = event.source;
    }

    onProviderCleared(): void {
        this.selectedProvider = null;
    }

    onSubmit(): void {
        if (!this.selectedProvider) return;

        this.loading = true;
        const data: ProviderData = {
            npi: this.selectedProvider.npi || '',
            name: this.selectedProvider.fullName || '',
            type: this.selectedProvider.providerType || '',
            credential: this.selectedProvider.credential || '',
            phone: this.selectedProvider.phone || '',
            fax: this.selectedProvider.fax || '',
            address: this.selectedProvider.practiceAddress || '',
            city: this.selectedProvider.city || '',
            state: this.selectedProvider.state || '',
            zip: this.selectedProvider.zip || '',
            notes: this.providerNotes,
            source: this.selectedSource,
        };
        const value = JSON.stringify(data);

        if (this.editMode && this.editId) {
            const userId = this.resolveUserId();
            this.leadService
                .editLeadProvider(userId, {
                    id: this.editId,
                    value,
                    userId,
                    statusId: 1,
                })
                .pipe(first(), takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.loading = false;
                        this.resetForm();
                        this.getProvidersList();
                        this.messageService.add({
                            key: 'tst',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Provider updated successfully',
                        });
                    },
                    error: () => {
                        this.loading = false;
                        this.messageService.add({
                            key: 'tst',
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Something went wrong, please try again',
                        });
                    },
                });
        } else {
            this.leadService
                .addLeadProvider(this.leadId, {
                    userId: this.leadId,
                    value,
                    statusId: 1,
                })
                .pipe(first(), takeUntil(this.onDestroy$))
                .subscribe({
                    next: () => {
                        this.loading = false;
                        this.resetForm();
                        this.getProvidersList();
                        this.messageService.add({
                            key: 'tst',
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Provider added successfully',
                        });
                    },
                    error: () => {
                        this.loading = false;
                    },
                });
        }
    }

    editProvider(prov: any): void {
        const data = this.parseProvider(prov.value);
        if (data) {
            this.editMode = true;
            this.editId = prov.id;
            this.providerNotes = data.notes || '';
            this.selectedSource = data.source || 'manual_entry';
            this.selectedProvider = {
                npi: data.npi,
                fullName: data.name,
                providerType: data.type,
                practiceAddress: data.address,
                credential: data.credential || null,
                firstName: null,
                lastName: null,
                gender: null,
                phone: data.phone || null,
                fax: data.fax || null,
                city: data.city || null,
                state: data.state || null,
                zip: data.zip || null,
            };
        }
    }

    deleteProvider(id: string): void {
        const userId = this.resolveUserId();
        this.confirmationService.confirm({
            message: 'Are you sure that you want to remove this provider?',
            header: 'Confirmation',
            key: 'leadProviderDelete',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.leadService
                    .deleteLeadProvider(userId, id)
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe({
                        next: () => {
                            this.getProvidersList();
                            this.messageService.add({
                                key: 'tst',
                                severity: 'success',
                                summary: 'Success',
                                detail: 'Successfully removed provider',
                            });
                        },
                        error: (error) => {
                            this.messageService.add({
                                key: 'tst',
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Error removing provider',
                            });
                            this.logger.log('error user providers', error);
                        },
                    });
            },
        });
    }

    resetForm(): void {
        this.selectedProvider = null;
        this.selectedSource = 'npi_lookup';
        this.providerNotes = '';
        this.editMode = false;
        this.editId = null;
    }

    cancelEdit(): void {
        this.resetForm();
    }

    private resolveUserId(): string {
        const getLatestLead = JSON.parse(localStorage.getItem('Launch_Dialer')) as LeadViewModel[];
        const getParams = this.activatedRoute.snapshot.params;
        if (getParams.hasOwnProperty('id')) {
            return getParams.id;
        } else if (getLatestLead) {
            return getLatestLead[0].id;
        }
        return this.leadId;
    }
}
